// redirect-decision-blocked-plan-supersede.ts
//
// Phase 06e R3 (F-25, spec E23). Dogfood run 4: plan T-0002 went `blocked`;
// the operator's redirect produced a decision ticket that created the real
// work, but nothing ever changed the plan's status, so G2 waited for "plan
// ready" until a SECOND redirect said `cancel T-0002`. The gate hint had
// promised one redirect was enough.
//
// One narrow rule: when the ticket finishing `done` is a decision the
// CONTROLLER wrote from a redirect (controller-redirect-decision-ticket.ts —
// ledger + read-only shape, never `kind`/`sources` alone), and THIS pass
// actually created ≥ 1 testcase/implement/test ticket (checked on the tickets
// after the batch remap — a replay that skipped the creates, or whose ticket
// files were cleaned, creates nothing ⇒ fail closed), every `blocked` plan
// gets one synthetic `supersede`, unless:
//   - the batch already targets that plan (no double op), or
//   - some live ticket depends on it (the scheduler only accepts `done`
//     deps — superseding would hang it silently): a notice instead, which the
//     caller emits only after the batch persists (06f R6 / L2). A retired
//     dependent (cancelled/superseded, per the status AFTER this pass) is
//     never dispatched, so it cannot hang and is ignored (review L1);
//   - the synthetic change is already receipted (crash-then-replay).
// Plans that are todo/in_progress/done are never touched (report Q3).
import type { AutopilotResult } from "./autopilot-result-schema.js";
import { controllerRedirectDecisionDirectiveId } from "./controller-redirect-decision-ticket.js";
import { isAcceptanceRequiredKind } from "./feature-ticket-acceptance-required.js";
import { RETIRED_TICKET_STATUSES } from "./g2-plan-readiness.js";
import type { MissionState } from "./mission-state-reducer.js";
import type { CreatedInBatch } from "./proposal-batch-target-remap.js";
import type { ProposedChange } from "./proposal-change-types.js";
import type { Ticket } from "./schemas/ticket-schema.js";

export interface BlockedPlanSupersedeInput {
  /** The ticket whose done attempt is being applied (undefined ⇒ no-op). */
  sourceTicket: Ticket | undefined;
  state: Pick<MissionState, "intents" | "results">;
  runId: string;
  /** Tickets AFTER every change of this pass + `remapBatchDependencies`. */
  tickets: readonly Ticket[];
  /** target→id pairs created by THIS pass (not by an earlier, replayed one). */
  created: readonly CreatedInBatch[];
  /** Start-of-tick projection — where a plan reads `blocked`. */
  ticketStatus: Record<string, string>;
  /** Status after every change of this pass — decides which dependents are retired. */
  currentStatus: Record<string, string>;
  proposedChanges: AutopilotResult["proposed_changes"];
}

export interface BlockedPlanSupersede {
  planId: string;
  operationId: string;
  change: ProposedChange;
  /** Operator line, emitted once the change is committed. */
  notice: string;
}

export function blockedPlanOperationId(runId: string, planId: string): string {
  return `apply-proposal-${runId}-blocked-plan-${planId}`;
}

export interface BlockedPlanSupersedePlan {
  supersedes: BlockedPlanSupersede[];
  /** 06f R6 (L2): operator lines for plans kept blocked by a live dependent — emit after persist. */
  dependentNotices: string[];
}

const NOTHING: BlockedPlanSupersedePlan = { supersedes: [], dependentNotices: [] };

export function blockedPlanSupersedeChanges(
  input: BlockedPlanSupersedeInput,
): BlockedPlanSupersedePlan {
  if (input.sourceTicket === undefined) return NOTHING;
  const directiveId = controllerRedirectDecisionDirectiveId(input.sourceTicket, input.state);
  if (directiveId === undefined) return NOTHING;

  const createdIds = new Set(input.created.map((c) => c.id));
  const createsSlice = input.tickets.some(
    (t) => createdIds.has(t.id) && isAcceptanceRequiredKind(t.kind),
  );
  if (!createsSlice) return NOTHING;

  const targeted = new Set(
    input.proposedChanges.filter((c) => c.op !== "create").map((c) => c.target),
  );
  const blockedPlans = input.tickets
    .filter((t) => t.kind === "plan" && input.ticketStatus[t.id] === "blocked")
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  const out: BlockedPlanSupersede[] = [];
  const dependentNotices: string[] = [];
  for (const plan of blockedPlans) {
    if (targeted.has(plan.id)) continue;
    const operationId = blockedPlanOperationId(input.runId, plan.id);
    if (input.state.results[operationId] !== undefined) continue; // already applied
    const liveDependent = input.tickets.some(
      (t) =>
        t.depends_on.includes(plan.id) &&
        !RETIRED_TICKET_STATUSES.has(input.currentStatus[t.id] ?? "todo"),
    );
    if (liveDependent) {
      dependentNotices.push(
        `plan ${plan.id} blocked có ticket phụ thuộc — chỉ đạo lại: cancel plan hoặc sửa depends_on`,
      );
      continue;
    }
    out.push({
      planId: plan.id,
      operationId,
      change: { op: "supersede", ticket_id: plan.id, expected_revision: plan.revision },
      notice:
        `plan ${plan.id} (blocked) ⇒ superseded: redirect ${directiveId} đã tạo ticket ` +
        "testcase/implement/test",
    });
  }
  return { supersedes: out, dependentNotices };
}
